import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  useWindowDimensions,
} from 'react-native';

import {
  isSupportingDocumentsPersistedProps,
  type SupportingDocumentsProps,
  type SupportingDocumentsPersistedProps,
  type SupportingDocumentsInitialProps,
} from './useSupportingDocuments';
import {
  AttachmentFlatList,
  AttachmentGroupedList,
} from '../../../components/attachments';
import { SubsectionForm, FileUpload } from '../../../components/forms';
import { Eye, EyeOff, List, Layers } from '../../../components/icons';
import { PermissionAwareField } from '../../../components/PermissionAwareField';
import { PersistedStepHeader } from '../../../components/PersistedStepHeader';
import { useRequestScope } from '../../../contexts/RequestScopeContext';
import {
  debugAttachmentGrouping,
  registerAttachmentGroupingDebugGlobals,
  summarizeGroupedAttachments,
} from '../../../dev/attachmentGroupingDebug';
import {
  debugAttachmentScope,
  logAttachmentViewToggle,
  registerAttachmentScopeDebugGlobals,
} from '../../../dev/attachmentScopeDebug';
import { useNetworkStatus } from '../../../hooks/queries/useOfflineRequestsQuery';
import {
  filterInlineAttachmentsFromSectionMap,
  type SavedAttachment,
} from '../../../services/attachmentService';
import { spacing } from '../../../theme';
import {
  buildGroupedAttachmentsFromSectionMap,
  coerceSectionAttachmentList,
  resolveAttachmentScopeOptions,
  resolveStoredStepIdsForAttachments,
} from '../../../utils/attachmentHelpers';
import { shouldIncludeAttachmentSectionInScope } from '../../../utils/facilityPowerAttachmentGrouping';
import { buildSupportingDocumentFlatRows } from '../../../utils/supportingDocumentsCount';

import type { SelectedFile } from '../../../components/forms/FileUpload';
import type { GroupedAttachments } from '../../../types/attachmentInterfaces';

/** Create Request — keep pre-#918 chrome (UOM top-right + upload card only; no PDF row). */
const SupportingDocumentsInitialLayout: React.FC<
  SupportingDocumentsInitialProps
> = ({
  formData: _formData,
  updateFormData: _updateFormData,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  disabled = false,
}) => {
  const isOnline = useNetworkStatus();

  return (
    <View style={initialLayoutStyles.container}>
      <Text style={initialLayoutStyles.pageTitle}>
        Supporting Documentation
      </Text>

      <SubsectionForm
        containerStyle={initialLayoutStyles.uploadCardShell}
        contentContainerStyle={initialLayoutStyles.uploadCardBody}
      >
        <FileUpload
          label="Upload Supporting Documents"
          requestSection="supporting-documents"
          onFilesSelected={
            onFilesSelected
              ? files => onFilesSelected('supporting-documents', files)
              : undefined
          }
          selectedFiles={selectedFiles['supporting-documents'] || []}
          placeholder="Click to upload documents"
          accept="all"
          maxSizeInMB={150}
          multiple={true}
          disabled={disabled || !isOnline}
          offlineMessage={
            !isOnline
              ? 'File uploads are not available in offline mode'
              : undefined
          }
        />
      </SubsectionForm>
    </View>
  );
};

const SupportingDocumentsPersistedLayout: React.FC<
  SupportingDocumentsPersistedProps
> = ({
  request,
  isEditing,
  editedData: _editedData,
  updateEditedData: _updateEditedData,
  showFullDetails,
  attachmentsBySection = {},
  selectedFiles = {},
  onFilesSelected,
  onRefetchAttachments,
  onDeleteAttachment,
  showViewToggle,
  isAdvancedView,
  onToggleView,
  offlineMode: _offlineMode = false,
  disabled: _disabled = false,
  onCaptionChange,
  pendingCaptionEdits = {},
}) => {
  const isOnline = useNetworkStatus();
  const { width: screenWidth } = useWindowDimensions();
  const isMobileWidth = screenWidth < 600;
  const [isGroupedView, setIsGroupedView] = useState(false);
  const requestScope = useRequestScope();
  const storedStepIds = useMemo(
    () =>
      resolveStoredStepIdsForAttachments({
        requestScopeStoredStepIds: requestScope?.storedStepIds,
        requestEnabledStepIds: request.enabledStepIds,
      }),
    [requestScope?.storedStepIds, request.enabledStepIds],
  );
  const requestStage = String(request.stage ?? request.sysRequestStage ?? '');

  const attachmentScopeOptions = useMemo(
    () =>
      resolveAttachmentScopeOptions({
        storedStepIds,
        requestStage,
      }),
    [storedStepIds, requestStage],
  );

  const listAttachmentsBySection = useMemo(() => {
    const coerced: Record<string, SavedAttachment[]> = {};
    Object.entries(attachmentsBySection).forEach(([sectionId, attachments]) => {
      coerced[sectionId] = coerceSectionAttachmentList(attachments);
    });
    return filterInlineAttachmentsFromSectionMap(coerced);
  }, [attachmentsBySection]);

  const handleCaptionUpdate = (attachmentId: number, caption: string) => {
    onCaptionChange?.(attachmentId, caption);
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    if (onDeleteAttachment) {
      await onDeleteAttachment(attachmentId);
    } else if (onRefetchAttachments) {
      await onRefetchAttachments();
    } else {
      console.warn('Attachment delete callback not provided');
    }
  };

  // Shared with the sidebar nav count (#1534) so `All Attachments (N)` here and
  // `Supporting Documents (N)` there can never disagree.
  const allAttachments = useMemo(
    () =>
      buildSupportingDocumentFlatRows(
        listAttachmentsBySection,
        attachmentScopeOptions,
      ),
    [listAttachmentsBySection, attachmentScopeOptions],
  );

  const scopedAttachmentsBySection = useMemo(() => {
    const scoped: Record<string, SavedAttachment[]> = {};
    Object.entries(listAttachmentsBySection).forEach(
      ([sectionId, attachments]) => {
        if (
          !shouldIncludeAttachmentSectionInScope(
            sectionId,
            attachmentScopeOptions,
          )
        ) {
          return;
        }
        scoped[sectionId] = coerceSectionAttachmentList(attachments);
      },
    );
    return scoped;
  }, [listAttachmentsBySection, attachmentScopeOptions]);

  const groupedAttachments = useMemo((): GroupedAttachments[] => {
    if (!isGroupedView) {
      return [];
    }
    const result = buildGroupedAttachmentsFromSectionMap(
      listAttachmentsBySection,
      attachmentScopeOptions,
    );
    if (typeof __DEV__ !== 'undefined' && __DEV__) {
      debugAttachmentGrouping('SupportingDocuments.groupedAttachments', {
        isGroupedView,
        flatCount: allAttachments.length,
        result: summarizeGroupedAttachments(result),
      });
    }
    return result;
  }, [
    isGroupedView,
    allAttachments.length,
    listAttachmentsBySection,
    attachmentScopeOptions,
  ]);

  useEffect(() => {
    registerAttachmentScopeDebugGlobals();
    registerAttachmentGroupingDebugGlobals();
    debugAttachmentScope('SupportingDocuments.persisted', {
      requestId: request.id,
      isGroupedView,
      requestEnabledStepIds: request.enabledStepIds ?? null,
      requestScopeStoredStepIds: requestScope?.storedStepIds ?? null,
      storedStepIds,
      enabledStepIds: attachmentScopeOptions.enabledStepIds,
      attachmentsBySection,
      allAttachmentsCount: allAttachments.length,
      scopedSectionKeys: Object.keys(scopedAttachmentsBySection),
      groupedGroupCount: groupedAttachments.length,
      groupedStageLabels: groupedAttachments.map(g => g.stageLabel),
    });
  }, [
    request.id,
    request.enabledStepIds,
    isGroupedView,
    requestScope?.storedStepIds,
    storedStepIds,
    attachmentScopeOptions.enabledStepIds,
    attachmentsBySection,
    allAttachments.length,
    scopedAttachmentsBySection,
    groupedAttachments,
  ]);

  const logViewToggle = useCallback(
    (nextGroupedView: boolean) => {
      const view = nextGroupedView ? 'grouped' : 'flat';
      logAttachmentViewToggle(
        `SupportingDocuments.viewToggle.${view}`,
        {
          view,
          requestId: request.id,
          requestEnabledStepIds: request.enabledStepIds ?? null,
          requestScopeStoredStepIds: requestScope?.storedStepIds ?? null,
          storedStepIds,
          enabledStepIds: attachmentScopeOptions.enabledStepIds,
          allAttachmentsCount: allAttachments.length,
          scopedSectionKeys: Object.keys(scopedAttachmentsBySection),
          groupedGroupCount: groupedAttachments.length,
          flatListSource: 'allAttachments',
          groupedListSource: 'groupedAttachments',
        },
        {
          attachmentsBySection,
          scopedAttachmentsBySection,
          attachmentScopeOptions,
          allAttachments,
          groupedAttachments,
          flatListRenderSource: allAttachments,
          groupedListRenderSource: groupedAttachments,
        },
      );
    },
    [
      request.id,
      request.enabledStepIds,
      requestScope?.storedStepIds,
      storedStepIds,
      attachmentScopeOptions,
      allAttachments,
      groupedAttachments,
      attachmentsBySection,
      scopedAttachmentsBySection,
    ],
  );

  const handleGroupedViewToggle = useCallback(() => {
    const nextGroupedView = !isGroupedView;
    setIsGroupedView(nextGroupedView);
    logViewToggle(nextGroupedView);
  }, [isGroupedView, logViewToggle]);

  return (
    <View style={persistedStyles.container}>
      <PersistedStepHeader
        title="Supporting Documentation"
        uomSlot={null}
        actionsSlot={
          showViewToggle && onToggleView ? (
            <TouchableOpacity
              style={persistedStyles.viewToggleButton}
              onPress={onToggleView}
            >
              {isAdvancedView ? (
                <Eye size={isMobileWidth ? 16 : 18} color="#666" />
              ) : (
                <EyeOff size={isMobileWidth ? 16 : 18} color="#666" />
              )}
              {!isMobileWidth && (
                <Text style={persistedStyles.viewToggleText}>
                  {isAdvancedView ? 'Advanced' : 'Basic'}
                </Text>
              )}
            </TouchableOpacity>
          ) : null
        }
      />

      <SubsectionForm
        title={`All Attachments${
          allAttachments.length > 0 ? ` (${allAttachments.length})` : ''
        }`}
        collapsibleSection={false}
        alwaysShow
        rightContent={
          <TouchableOpacity
            style={[
              persistedStyles.groupToggleButton,
              isGroupedView && persistedStyles.groupToggleButtonActive,
            ]}
            onPress={handleGroupedViewToggle}
            accessibilityLabel={
              isGroupedView
                ? 'Switch to flat list view'
                : 'Switch to grouped view'
            }
            accessibilityState={{ selected: isGroupedView }}
          >
            {isGroupedView ? (
              <List size={14} color="#666" />
            ) : (
              <Layers size={14} color="#ff6b35" />
            )}
            <Text
              style={[
                persistedStyles.groupToggleText,
                isGroupedView && persistedStyles.groupToggleTextActive,
              ]}
            >
              {isGroupedView ? 'Flat' : 'Grouped'}
            </Text>
          </TouchableOpacity>
        }
      >
        {isGroupedView && (
          <AttachmentGroupedList
            groupedAttachments={groupedAttachments}
            isEditing={isEditing}
            isMobile={isMobileWidth}
            onCaptionChange={handleCaptionUpdate}
            onDelete={handleDeleteAttachment}
            pendingCaptionEdits={pendingCaptionEdits}
            offlineMode={!isOnline}
          />
        )}

        {!isGroupedView && (
          <AttachmentFlatList
            attachments={allAttachments}
            isEditing={isEditing}
            showSectionBadges={false}
            isMobile={isMobileWidth}
            onCaptionChange={handleCaptionUpdate}
            onDelete={handleDeleteAttachment}
            pendingCaptionEdits={pendingCaptionEdits}
            offlineMode={!isOnline}
          />
        )}

        {isEditing && onFilesSelected && (
          <View style={persistedStyles.uploadSection}>
            <PermissionAwareField
              fieldId="supportingDocuments"
              requestStage={request.stage || request.sysRequestStage}
              requestId={request.id}
              showFullDetails={showFullDetails}
            >
              <FileUpload
                label="Upload Documents"
                requestSection="supporting-documents"
                onFilesSelected={files =>
                  onFilesSelected('supporting-documents', files)
                }
                selectedFiles={selectedFiles['supporting-documents'] || []}
                placeholder="Click to upload documents (audio transcriptions, PDFs, images, etc.)"
                accept="all"
                maxSizeInMB={100}
                multiple={true}
                disabled={!isOnline}
                offlineMessage={
                  !isOnline
                    ? 'File uploads are not available in offline mode'
                    : undefined
                }
              />
            </PermissionAwareField>
          </View>
        )}
      </SubsectionForm>
    </View>
  );
};

export const SupportingDocuments: React.FC<SupportingDocumentsProps> = props =>
  isSupportingDocumentsPersistedProps(props) ? (
    <SupportingDocumentsPersistedLayout {...props} />
  ) : (
    <SupportingDocumentsInitialLayout {...props} />
  );

SupportingDocuments.displayName = 'SupportingDocuments';

/** @deprecated Tests — compose path only */
export const SupportingDocumentsStep = SupportingDocumentsInitialLayout;

/** @deprecated Tests — persisted branch */
export const SupportingDocumentsInRequest = SupportingDocumentsPersistedLayout;

const initialLayoutStyles = StyleSheet.create({
  /** Do not grow to fill the scroll viewport — upload card stays content-sized (pre-#918). */
  container: {
    width: '100%',
    alignSelf: 'stretch',
  },
  uomContainer: {
    alignSelf: 'flex-end',
    marginBottom: 30,
    maxWidth: 240,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#ff6b35',
    marginBottom: spacing.belowFormStepPageTitleRelaxed,
  },
  uploadCardShell: {
    alignSelf: 'stretch',
    flexGrow: 0,
    flexShrink: 0,
  },
  uploadCardBody: {
    flexGrow: 0,
  },
});

const persistedStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  rightButtonsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    flexShrink: 0,
  },
  viewToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Platform.OS === 'web' ? 6 : 3,
    paddingHorizontal: Platform.OS === 'web' ? 12 : 6,
    paddingVertical: Platform.OS === 'web' ? 6 : 3,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f8f9fa',
  },
  viewToggleText: {
    fontSize: Platform.OS === 'web' ? 13 : 9,
    fontWeight: '500',
    color: '#666',
  },
  viewToggleButtonActive: {
    borderColor: '#007bff',
    backgroundColor: '#e7f1ff',
  },
  viewToggleTextActive: {
    color: '#007bff',
  },
  pdfButtonContainer: {},
  pageTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#ff6b35',
    marginBottom: spacing.belowFormStepPageTitle,
  },
  groupToggleButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#f8f9fa',
    flexShrink: 0,
  },
  groupToggleButtonActive: {
    borderColor: '#ff6b35',
    backgroundColor: '#fff5f0',
  },
  groupToggleText: {
    fontSize: 11,
    fontWeight: '500',
    color: '#666',
  },
  groupToggleTextActive: {
    color: '#ff6b35',
  },
  uploadSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
});
